import React, { useMemo, useState } from "react";
import { Box, Container, useTheme, useMediaQuery } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import {
  useBookingList,
  useRespondToBooking,
  useStartEndStageSession,
} from "../../../../services/stagemode_api/talent/talent_api";
import CustomTab from "../../../common/CustomTab";
import TableSkeleton from "../../../TableSkeleton/TableSkeleton";
import Empty from "../../../Empty/Empty";
import StageBookingCard from "./StageBookingCard";
import CustomTabPanel from "../../Talent/BookingList/CustomTabPanel";
import { useCurrentTalentProfile } from "../../../../hooks";
import { useNavigate } from "react-router-dom";
import useCurrentBusinessInfo from "../../../../hooks/useCurrentBusiness";

export const TabMapper = {
  ACCEPTED: 0,
  PENDING: 1,
  ONGOING: 2,
  COMPLETED: 3,
  CANCELLED: 4,
};

const StageMoodBookingList: React.FC = () => {
  const [value, setValue] = useState<number | string>(TabMapper.PENDING);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const navigate = useNavigate();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { currentProfile } = useCurrentTalentProfile();
  const { currentBusiness } = useCurrentBusinessInfo();

  const respondToBooking = useRespondToBooking(currentProfile?.id);
  const startEndStageSession = useStartEndStageSession(
    currentProfile?.id.toString()
  );

  const getStatusFromTab = (tabValue: number) => {
    switch (tabValue) {
      case TabMapper.ACCEPTED:
        return "Scheduled";
      case TabMapper.PENDING:
        return "Pending";
      case TabMapper.ONGOING:
        return "Ongoing";
      case TabMapper.COMPLETED:
        return "Completed";
      case TabMapper.CANCELLED:
        return "Cancelled";
      default:
        return "Pending";
    }
  };

  const { data, isLoading } = useBookingList(
    getStatusFromTab(value as number),
    currentProfile?.id,
    currentBusiness?.id.toString()
  );

  const bookings = useMemo(() => data?.data || [], [data?.data]);

  const bookingTabs = [
    { label: "Accepted", value: 0 },
    { label: "Pending", value: 1 },
    { label: "Ongoing", value: 2 },
    { label: "Completed", value: 3 },
    { label: "Cancelled", value: 4 },
  ];

  const handleToggle = (id: number) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const handleAccept = async (id: number) => {
    try {
      await respondToBooking.mutateAsync({
        bookingId: id.toString(),
        status: "accept",
        ...(currentBusiness?.id && {
          business_id: currentBusiness?.id.toString(),
        }),
      });
      enqueueSnackbar("Booking accepted successfully", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to accept booking", { variant: "error" });
    }
  };

  const handleReject = async (id: number) => {
    try {
      await respondToBooking.mutateAsync({
        bookingId: id.toString(),
        status: "reject",
        ...(currentBusiness?.id && {
          business_id: currentBusiness?.id.toString(),
        }),
      });
      enqueueSnackbar("Booking rejected successfully", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to reject booking", { variant: "error" });
    }
  };

  const handleStartCall = async (id: number, channel: string) => {
    try {
      const res = await startEndStageSession.mutateAsync({
        bookingId: id.toString(),
        status: "start",
        ...(currentBusiness?.id && {
          business_id: currentBusiness?.id.toString(),
        }),
      });
      const uid = res.data?.data?.agora_uid;

      const booking = bookings.find((b: any) => b.id === id);
      const stagecardType = booking?.stagecard?.type;

      if (stagecardType === "Sing4You") {
        navigate(`/singforyoulive/${channel}?userId=${uid}&bookingId=${id}`);
      } else {
        navigate(`/stage-live-talent/${channel}?userId=${uid}&bookingId=${id}`);
      }
    } catch (error) {
      enqueueSnackbar("Failed to start call", { variant: "error" });
    }
  };

  const bookingsByStatus = useMemo(() => {
    if (!bookings) return [];

    const statusMap = {
      [TabMapper.PENDING]: "pending",
      [TabMapper.ACCEPTED]: "scheduled",
      [TabMapper.ONGOING]: "ongoing",
      [TabMapper.COMPLETED]: "completed",
      [TabMapper.CANCELLED]: "cancelled",
    };

    const filteredBookings = bookings.filter(
      (booking: any) =>
        booking.status?.toLowerCase() ===
        statusMap[value as keyof typeof statusMap]
    );

    return filteredBookings;
  }, [bookings, value]);

  const handleChange = (_: React.SyntheticEvent, newValue: number | string) =>
    setValue(newValue);

  // —— Netflix-ish tokens (visual only)
  const COLORS = {
    bg: "#0b0b0b",
    text: "#ffffff",
    muted: "rgba(255,255,255,0.75)",
    border: "rgba(255,255,255,0.12)",
    red: "#E50914",
  };

  return (
    <Box
      width="100%"
      py={4}
      px={isMobile ? 2 : 0}
      sx={{
        backgroundColor: COLORS.bg,
        color: COLORS.text,
        position: "relative",
        // subtle hero glow
        "&:before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(1000px 420px at 8% -10%, rgba(229,9,20,.08), transparent 60%)",
          pointerEvents: "none",
        },
      }}
    >
      <Container
        sx={{
          position: "relative",
          zIndex: 1,
          color: COLORS.text,
        }}
      >
        {/* Tabs (already restyled in your CustomTab Netflix pass) */}
        <CustomTab tabs={bookingTabs} value={value} onChange={handleChange} />

        {isLoading ? (
          <Box mt={3}>
            <TableSkeleton />
          </Box>
        ) : (
          <CustomTabPanel value={value} index={value}>
            <Box
              display="flex"
              flexDirection="column"
              gap={3}
              width="100%"
              py={1}
              sx={{
                color: COLORS.text,
                // give the list area a faint separation
                borderTop: `1px solid ${COLORS.border}`,
                mt: 2,
              }}
            >
              {bookingsByStatus.length ? (
                bookingsByStatus.map((booking: any) => (
                  <StageBookingCard
                    key={booking.id}
                    booking={booking}
                    expanded={expandedId === booking.id}
                    onToggle={() => handleToggle(booking.id)}
                    tabType={
                      value === TabMapper.ACCEPTED
                        ? "ACCEPTED"
                        : value === TabMapper.PENDING
                          ? "PENDING"
                          : value === TabMapper.ONGOING
                            ? "ONGOING"
                            : value === TabMapper.COMPLETED
                              ? "COMPLETED"
                              : "CANCELLED"
                    }
                    onAccept={handleAccept}
                    onReject={handleReject}
                    onStartCall={handleStartCall}
                  />
                ))
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 6,
                    borderRadius: 2,
                    background:
                      "linear-gradient(180deg, rgba(20,20,20,0.9), rgba(11,11,11,0.9))",
                    border: `1px solid ${COLORS.border}`,
                    color: COLORS.text,
                    "& *": {
                      color: `${COLORS.text} !important`,
                    },
                    "& .MuiSvgIcon-root": {
                      color: `${COLORS.muted} !important`,
                    },
                  }}
                >
                  <Empty message="No Stage Bookings available" />
                </Box>
              )}
            </Box>
          </CustomTabPanel>
        )}
      </Container>
    </Box>
  );
};

export default StageMoodBookingList;
